import { type FunctionComponent, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { type ServerAnalyticsInterface } from '../../../../../@types/build';
import { type Maybe, type ProductViewEnum, type SearchVariantFieldsFragment } from '../../../__generated__/graphql-client-types';
import { FEATURE_FLAGS } from '../../../constants/general';
import { ImpressionsContext } from '../../../contexts/impressions/impressions.context';
import { SearchVariantContext } from '../../../contexts/search/search-variant.context';
import { ANALYTICS_PAGE_NAMES, type AnalyticsEvent } from '../../../helpers/analytics/analytics.helper';
import { TrackedEvent } from '../../../helpers/analytics/event-types';
import { type GTMEventWrapper } from '../../../helpers/analytics/gtm/gtm-types';
import { setUrlParam } from '../../../helpers/general-helper/general-helper';
import { ADD_TO_CART_BUTTON_TYPE } from '../../../helpers/product-helper/add-to-cart-button/add-to-cart-button.helper';
import { getFindingMethodEventByDropType } from '../../../helpers/search-helper/search-analytics.helper';
import { useAddToCartButton } from '../../../hooks/add-to-cart/add-to-cart-button.hooks';
import { useAddToCartAction } from '../../../hooks/add-to-cart/add-to-cart.hooks';
import { type UseSearchResultsPayload } from '../../../hooks/apollo/search/search.hooks';
import { useFeature } from '../../../hooks/features/features.hooks';
import { useNavigation } from '../../../hooks/navigation/navigation.hooks';
import { useNotification } from '../../../hooks/notification/notification.hooks';
import { TRACK_SEARCH_RESULT_CLICK } from '../../../queries/search/search-tracking.queries';
import { type DropType, type SearchProduct } from '../../../types/search.types';
import { Link } from '../../common-components/link/link.component';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';
import { ColorText } from '../../product-card/product-line-items/color-text/color-text.component';
import { Model } from '../../product-card/product-line-items/model/model.component';
import { NonstockBadge } from '../../product-card/product-line-items/nonstock-badge/nonstock-badge.component';
import { Price } from '../../product-card/product-line-items/price/price.component';
import { ProductLineItems } from '../../product-card/product-line-items/product-line-items.component';
import { RebateWidget } from '../../product-card/product-line-items/rebate-widget/rebate-widget.component';
import { ReviewStarRating } from '../../product-card/product-line-items/review-star-rating/review-star-rating.component';
import { Shipping } from '../../product-card/product-line-items/shipping/shipping.component';
import { Swatches } from '../../product-card/product-line-items/swatches/swatches.component';
import { Title } from '../../product-card/product-line-items/title/title.component';
import { AddToCartButtonFactory } from '../../product-components/add-to-cart-button/add-to-cart-button.component';
import { CompareButton } from '../../product-components/compare-button/compare-button.component';
import { cardShadow } from './search-product-card.css';

function createAnalyticsEvents(...gtmEvents: (GTMEventWrapper | null | undefined)[]): AnalyticsEvent[] {
	return gtmEvents.filter((evt?: GTMEventWrapper | null): evt is GTMEventWrapper => Boolean(evt)).map((evt) => ({ data: evt }));
}

function buildProductUrl(baseUrl: string, searchId: string | null | undefined, isNonstockSearch: boolean): string {
	let url = baseUrl;
	if (searchId) {
		url = setUrlParam(url, 'searchId', searchId);
	}
	if (isNonstockSearch) {
		url = setUrlParam(url, 'ns', 'true');
	}
	return url;
}

export type SearchProductCardProps = {
	product: SearchProduct;
	dropType?: DropType;
	cardIndex: number;
	page?: number;
	viewType?: Maybe<ProductViewEnum>;
	searchId?: string | null;
	isNonstockSearch?: boolean;
	isNewLookAndFeel?: boolean;
	createGTMEvent: UseSearchResultsPayload['createGTMEvent'];
};

const ProductAttributes: FunctionComponent<{ product: SearchProduct }> = ({ product }) => (
	<div className={'dn db-ns w-25 pa2 bg-theme-grey-lighter'} data-testid="searchProductCardAttributes">
		<ul className={'f6 mv0 pl4'}>
			{product.collection && (
				<li className="pa1">
					<StyledLink url={product.collection.url} color="primary">
						{product.collection.name} Collection
					</StyledLink>
				</li>
			)}
			{product.parts?.url && (
				<li className="pa1">
					<StyledLink url={product.parts.url} color="primary">
						{product.parts.name}
					</StyledLink>
				</li>
			)}
			{product.attributes &&
				product.attributes.map((attr, index) => (
					<li className={'pa1 theme-grey-darker'} key={`${attr.name}:${index}`}>
						{attr.name}
						{attr.values && `: ${attr.values}`}
					</li>
				))}
		</ul>
	</div>
);

const AddToCartSection: FunctionComponent<{
	handleAddToCartClick: any;
	isConfigurable: boolean;
	hasSalesRestrictions: boolean;
	isShowroomOnlyProduct: boolean;
	loading: boolean;
	busyText?: string;
	variant: SearchVariantFieldsFragment;
}> = ({ handleAddToCartClick, isShowroomOnlyProduct, isConfigurable, hasSalesRestrictions, loading, busyText, variant }) => {
	const { addToCartActionStatus } = useAddToCartButton({ pageName: 'PLP' });
	return (
		<AddToCartButtonFactory
			buttonType={variant.productPricingDisplay}
			addToCartButtonType={ADD_TO_CART_BUTTON_TYPE.PLP}
			handleOnClick={handleAddToCartClick}
			disabled={false}
			status={addToCartActionStatus}
			isConfigurable={isConfigurable}
			hasSalesRestrictions={hasSalesRestrictions}
			isShowroomOnlyProduct={isShowroomOnlyProduct}
			isBusy={loading}
			busyText={busyText}
		/>
	);
};

export const SearchProductCard: FunctionComponent<SearchProductCardProps> = ({
	product,
	dropType = 'search',
	viewType = 'TILE',
	cardIndex,
	page,
	searchId,
	isNonstockSearch = false,
	isNewLookAndFeel = false,
	createGTMEvent
}) => {
	const { addToCart, status } = useAddToCartAction();

	const navigate = useNavigation();

	const { hoveredVariant, selectedVariant } = useContext(SearchVariantContext);
	const variant = hoveredVariant || selectedVariant;

	const ecoRebatesWidgetsActive = useFeature(FEATURE_FLAGS.ECOREBATES_WIDGETS);

	const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false);

	const { notifySuccess, notifyDanger } = useNotification();

	const { id, brandName, title, modelNumber, attributes, familyId, priceInfo, variants, isConfigurable } = product;

	const isTileView = viewType === 'TILE';
	const isListView = !isTileView;

	const imageVariations = [
		{
			options: {
				width: 160,
				height: 160
			},
			mediaCondition: {
				maxWidth: 30,
				unit: 'em' as const
			}
		}
	];

	const price = variant.priceInfo.unitPrice ? variant.priceInfo.unitPrice.price : variant.priceInfo.current;
	const priceUnits = priceInfo.unitPrice?.units;
	const contactForPricing = variant.productPricingDisplay !== 'STANDARD' && variant.productPricingDisplay !== 'CALL_FOR_PURCHASE';
	const overridePriceMessage = contactForPricing ? 'Contact for Pricing' : undefined;
	const hasSalesRestriction = variant?.isProductRestricted;
	const nonstockCount = variants?.filter((v) => v.status !== 'stock').length ?? 0;
	const variantCount = variants?.length ?? 0;
	const isShowroomOnlyProduct = variant.productPricingDisplay === 'SHOW_ROOM_ONLY';

	const hasShippingData = product.shippingInfo?.isQuickShip || product.shippingInfo?.message || hasSalesRestriction;
	const showShippingInfo = !isShowroomOnlyProduct && !isNewLookAndFeel && hasShippingData;

	let serverAnalytics: ServerAnalyticsInterface | undefined;
	if (searchId) {
		serverAnalytics = {
			mutation: TRACK_SEARCH_RESULT_CLICK,
			variables: { input: { searchId, familyId, uniqueId: variant.id, page: page ?? 1, pagePosition: cardIndex } }
		};
	}
	const { addImpression } = useContext(ImpressionsContext);
	const { ref, inView } = useInView({
		triggerOnce: true
	});

	useEffect(() => {
		if (inView) {
			addImpression(product, cardIndex + 1);
		}
	}, [addImpression, inView, product, cardIndex]);

	const productClickEvent = createGTMEvent(TrackedEvent.PRODUCT_CLICK, { product, dropType, cardIndex });
	const findingMethodEvent = getFindingMethodEventByDropType(dropType);
	const analyticsEvent = createAnalyticsEvents(productClickEvent, findingMethodEvent);

	const url = buildProductUrl(variant.url, searchId, isNonstockSearch);

	const tileDetailsPadding = isNonstockSearch ? 'pb3-ns' : 'pa0-ns';
	const detailsSectionClassName = isTileView ? `w-100 h-100 h-50-l ${tileDetailsPadding}` : 'w-50';

	const analyticsData = {
		variantId: variant.id,
		pageName: ANALYTICS_PAGE_NAMES.PRODUCT_LISTINGS_PAGE,
		link: 'Add to Cart',
		linkName: 'Add to Cart',
		parentId: product.familyId
	};

	const handleAddToCartClick = () => {
		if (isConfigurable) {
			createGTMEvent(TrackedEvent.PLP_CLICKED_CONFIGURE);
		}
		if (isConfigurable || hasSalesRestriction || isShowroomOnlyProduct) {
			navigate(variant.url);
			return;
		}

		addToCart({ variantId: variant.id, quantity: 1, analyticsData })
			.then(() => notifySuccess('Item successfully added to cart!', 5000))
			.catch(() => notifyDanger('There was an issue adding the item to the cart.', 5000));
	};

	const getCardContainerClasses = () => {
		const baseClasses = 'bg-theme-white bb-ns b--theme-grey-light relative';
		if (isTileView) {
			const tileClasses = `w-50 w-25-ns pv1 ${cardIndex % 2 === 0 ? 'pr1' : 'pl1'} pa2-ns mb3-ns`;
			return `${tileClasses} ${baseClasses}`;
		}
		const listClasses = 'mt2 mt3-ns ba bl-0-ns br-0-ns bt-0-ns br2 br0-ns w-100';
		return `${listClasses} ${baseClasses}`;
	};

	const getCardInnerClasses = () => {
		const baseClasses = 'flex h-100';
		if (isTileView) {
			return `${baseClasses} relative flex-column pa2 pa0-ns ba b--theme-grey-light br2 b--none-ns ${cardShadow}`;
		}
		return `${baseClasses} flex-row w-100`;
	};

	const getSwatchesClasses = () => {
		if (isListView) {
			return 'dn db-ns';
		}
		return variants.length > 1 ? 'h2-ns flex-ns items-center-ns' : '';
	};

	const getColorTextClasses = () => {
		if (isListView) {
			return 'dn db-ns';
		}
		return variants.length === 1 ? 'h2-ns flex-ns items-center-ns' : '';
	};

	return (
		<div
			ref={ref}
			className={getCardContainerClasses()}
			data-automation={`product-card-${modelNumber.toLowerCase()}`}
			onMouseEnter={() => {
				setIsBottomDrawerOpen(true);
			}}
			onMouseLeave={() => {
				setIsBottomDrawerOpen(false);
			}}
			role="button"
			tabIndex={0}
			data-testid="searchProductCard">
			<div className={getCardInnerClasses()}>
				{/* Image and Finishes section*/}
				<div className={`${isListView ? 'w-50 w-25-ns ph2 pr3-ns pv2 tc' : ''}`}>
					<Link url={url} className="db center mw5" serverAnalytics={serverAnalytics} analyticsEvent={analyticsEvent}>
						<div className="aspect-ratio aspect-ratio--1x1 tc">
							<div className="aspect-ratio--object z-1">
								<CloudinaryImage
									publicID={variant.image.id.toString()}
									description={variant.image.description}
									options={{ height: 214, width: 214 }}
									useDimensions={false}
									variations={imageVariations}
									bypassLazyLoad={cardIndex < 8}
								/>
							</div>
						</div>
					</Link>
					{isListView && (
						<>
							<div className="mv2 h2-ns dn-ns">
								{variants?.length > 1 && (
									<>
										<Swatches
											variants={variants}
											maxNumberToDisplay={5}
											isTileView={false}
											scrollEvent={TrackedEvent.PLP_SCROLLED_THROUGH_FINISHES}
											selectEvent={TrackedEvent.PLP_SELECTED_FINISH}
										/>
										<ColorText colorName={variant.name} mobileOnly={variants.length > 1} />
									</>
								)}
							</div>
							<div className="center dn db-ns">
								<CompareButton variantId={product.id} hasBorder={false} className="pa3 underline-hover" />
							</div>
						</>
					)}
				</div>
				{/* Details section*/}
				<div className={`${detailsSectionClassName}`}>
					<div className={`flex flex-column justify-between w-100 h-100`}>
						<div className={`${isListView ? 'pl3 pr2' : ''} pt2`}>
							<ProductLineItems
								fixedTitleModelHeight={isTileView}
								Swatches={
									<Swatches
										variants={variants}
										maxNumberToDisplay={5}
										isTileView={isTileView}
										scrollEvent={TrackedEvent.PLP_SCROLLED_THROUGH_FINISHES}
										selectEvent={TrackedEvent.PLP_SELECTED_FINISH}
										className={getSwatchesClasses()}
									/>
								}
								ColorText={
									<ColorText
										colorName={variant.name}
										mobileOnly={variants.length > 1}
										className={getColorTextClasses()}
									/>
								}
								Title={
									<Title
										url={url}
										brandName={brandName}
										title={title}
										titleTruncation="tc3-title"
										fontSize="f5"
										automationHook="product-card-description"
										serverAnalytics={serverAnalytics}
										analyticsEvent={analyticsEvent}
									/>
								}
								Model={<Model modelNumber={variant.modelNumber} truncate />}
								NonstockBadge={
									isListView && isNonstockSearch && nonstockCount > 0 ? (
										<NonstockBadge nonstockCount={nonstockCount} variantCount={variantCount} />
									) : undefined
								}
								ReviewStarRating={
									!isShowroomOnlyProduct ? (
										<ReviewStarRating
											reviewCount={product.rating.reviewCount}
											ratingValue={product.rating.ratingValue}
											productUrl={url}
										/>
									) : undefined
								}
								Price={
									!isShowroomOnlyProduct && price ? (
										<Price unitPrice={price} units={priceUnits} overridePriceMessage={overridePriceMessage} />
									) : undefined
								}
								RebateWidget={
									!isShowroomOnlyProduct && ecoRebatesWidgetsActive ? (
										<RebateWidget className="mb1" variantIds={variants.map((v) => v.id)} />
									) : undefined
								}
								Shipping={
									showShippingInfo ? (
										<Shipping
											hasSalesRestriction={hasSalesRestriction}
											isQuickShip={Boolean(variant.isQuickShip)}
											message={variant?.estimatedDeliveryMessage || ''}
										/>
									) : undefined
								}
							/>
						</div>
						{!isNewLookAndFeel && isListView && (
							<div className="dn db-ns">
								<div className="pl3 pb3">
									<div className="w5 pt2">
										<AddToCartSection
											handleAddToCartClick={handleAddToCartClick}
											hasSalesRestrictions={hasSalesRestriction}
											isShowroomOnlyProduct={isShowroomOnlyProduct}
											variant={variant}
											isConfigurable={Boolean(isConfigurable)}
											loading={status.loading}
										/>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
				{/* Product Attributes; hidden on mobile */}
				{isListView && attributes && attributes.length > 0 && <ProductAttributes product={product} />}
				{isTileView && isNonstockSearch && nonstockCount > 0 && (
					<NonstockBadge
						nonstockCount={nonstockCount}
						variantCount={variantCount}
						className="z-1 absolute top-0 right-0 top--1-ns disableClickEvents"
					/>
				)}
				{!isNonstockSearch && isTileView && !isNewLookAndFeel && (
					<div className={`w-100 mt2 dn-l`}>
						<AddToCartSection
							handleAddToCartClick={handleAddToCartClick}
							variant={variant}
							isConfigurable={Boolean(isConfigurable)}
							isShowroomOnlyProduct={isShowroomOnlyProduct}
							hasSalesRestrictions={hasSalesRestriction}
							loading={status.loading}
							busyText={'Adding...'}
						/>
						<div className={`self-end tc`}>
							<CompareButton variantId={id} hasBorder={false} className="pa3 underline-hover mt2-ns" />
						</div>
					</div>
				)}
			</div>
			{/* Section only shows in mobile List View */}
			{!isNonstockSearch && isListView && (
				<div className="dn-ns">
					<div className="flex justify-center">
						<div className="w-100 mw5 mt2 ph2">
							<AddToCartSection
								handleAddToCartClick={handleAddToCartClick}
								variant={variant}
								hasSalesRestrictions={hasSalesRestriction}
								isConfigurable={Boolean(isConfigurable)}
								loading={status.loading}
								isShowroomOnlyProduct={isShowroomOnlyProduct}
							/>
						</div>
					</div>
					<div className={`flex justify-center mb2`}>
						<CompareButton variantId={id} hasBorder={false} className="pa3 pb2 underline-hover" />
					</div>
				</div>
			)}
			{isTileView && isBottomDrawerOpen && (
				<div
					className={`z-2 bb br bl b--theme-grey-light bg-theme-white left-0 right-0 w-auto absolute mt2 dn db-l`}
					style={{ boxShadow: '0px 10px 10px -10px rgba( 0, 0, 0, 0.2 )' }}>
					<div className="w-100 pa2 pt0" data-testid="bottom-add-to-cart-drawer">
						<AddToCartSection
							handleAddToCartClick={handleAddToCartClick}
							variant={variant}
							isShowroomOnlyProduct={isShowroomOnlyProduct}
							isConfigurable={Boolean(isConfigurable)}
							loading={status.loading}
							hasSalesRestrictions={hasSalesRestriction}
						/>
						{!isNonstockSearch && (
							<div className={'flex justify-center pv1'}>
								<CompareButton variantId={id} hasBorder={false} className="pa2 pb0 underline-hover" />
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};
